import process from 'node:process';
import { readFileSync } from 'node:fs';
import type { EmulationPluginEntry, EmulationTemplateConfig } from '@chatsdet/emulation';
import { getAllPlugins, getPluginIds } from '@chatsdet/emulation/plugins';
import { getArgValue, hasFlag } from './auth.js';
import { apiRequest } from './cli-helpers.js';

/** One configured plugin as enriched by GET .../emulation (descriptions only, never secret values). */
type EmulationCatalogEntry = {
  pluginId: string;
  name: string;
  version: string;
  enabled: boolean;
  envContract: Array<{
    logicalKey: string;
    strategy: string;
    sensitive: boolean;
    description?: string;
  }>;
  inboundEvents: Array<{ id: string; description?: string }>;
  actionInterfaces: Array<{ slug: string; implemented: boolean }>;
};

const pad = (value: string, width: number): string => value.padEnd(width);

/**
 * Read the template's stored emulation config + plugin catalog. The GET route
 * returns `{ ok, config, catalog }`, not a `data` wrapper. Falls back to the
 * empty default config when the template has none.
 */
async function fetchEmulation(
  apiUrl: string,
  token: string,
  templateId: string
): Promise<{ config: EmulationTemplateConfig; catalog: EmulationCatalogEntry[] }> {
  const res = await apiRequest<{
    ok: boolean;
    error?: string;
    config?: EmulationTemplateConfig;
    catalog?: EmulationCatalogEntry[];
  }>(apiUrl, token, 'GET', `/org/environments/templates/${templateId}/emulation`);

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }

  // Fresh literal (not a shared module singleton) so callers/transforms can
  // never accidentally mutate a process-wide default.
  return { config: res.config ?? { enabled: false, plugins: [] }, catalog: res.catalog ?? [] };
}

/**
 * Write the full emulation config back. The PATCH route is a full replace of
 * `{ enabled, plugins[] }`, so every mutating command must read-modify-write
 * the whole object.
 */
async function patchEmulation(
  apiUrl: string,
  token: string,
  templateId: string,
  config: EmulationTemplateConfig
): Promise<void> {
  const res = await apiRequest<{ ok: boolean; error?: string }>(
    apiUrl,
    token,
    'PATCH',
    `/org/environments/templates/${templateId}/emulation`,
    { enabled: config.enabled, plugins: config.plugins },
    { timeoutMs: 90_000 }
  );

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }
}

/** Reject a pluginId that is not a known emulation plugin, with the valid list. */
function assertKnownPlugin(pluginId: string): void {
  const ids = getPluginIds();
  if (!ids.includes(pluginId)) {
    console.error(`Error: unknown plugin '${pluginId}'. Available: ${ids.join(', ')}`);
    process.exit(1);
  }
}

function readJsonFileOrExit(path: string, flag: string): unknown {
  let raw: string;
  try {
    raw = readFileSync(path, 'utf-8');
  } catch (err) {
    console.error(
      `Error reading ${flag} file '${path}': ${err instanceof Error ? err.message : String(err)}`
    );
    process.exit(1);
  }
  try {
    return JSON.parse(raw);
  } catch (err) {
    console.error(
      `Error parsing ${flag} file '${path}' as JSON: ${
        err instanceof Error ? err.message : String(err)
      }`
    );
    process.exit(1);
  }
}

/**
 * Like `readJsonFileOrExit` but rejects anything that is not a plain JSON object
 * (arrays, strings, numbers, null all parse fine but would fail the server's
 * `z.record(...)` validation with a cryptic error). Used for `--config` and
 * `--env-mapping`; `--fixture` stays unrestricted since fixtures are arbitrary.
 */
function readJsonObjectOrExit(path: string, flag: string): Record<string, unknown> {
  const parsed = readJsonFileOrExit(path, flag);
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    console.error(
      `Error: ${flag} file '${path}' must contain a JSON object, not ${
        Array.isArray(parsed) ? 'an array' : typeof parsed
      }.`
    );
    process.exit(1);
  }
  return parsed as Record<string, unknown>;
}

// The PATCH route is a full replace, so each mutating command produces a fresh
// EmulationTemplateConfig from the fetched one. These are pure so the
// read-modify-write semantics can be tested without network/IO.

/** Flip the master switch, leaving plugin entries untouched. */
export function withEmulationMaster(
  config: EmulationTemplateConfig,
  enabled: boolean
): EmulationTemplateConfig {
  return { enabled, plugins: [...(config.plugins ?? [])] };
}

/**
 * Toggle one plugin entry's `enabled`. Enabling a not-yet-configured plugin
 * adds a bare entry; enabling any plugin also turns the master switch on.
 * Disabling assumes the entry exists (callers validate first for a friendly error).
 */
export function withPluginEnabled(
  config: EmulationTemplateConfig,
  pluginId: string,
  enabled: boolean
): EmulationTemplateConfig {
  const plugins = [...(config.plugins ?? [])];
  const idx = plugins.findIndex((plugin) => plugin.pluginId === pluginId);
  if (idx === -1) {
    if (enabled) plugins.push({ pluginId, enabled: true, config: {} });
  } else {
    plugins[idx] = { ...plugins[idx], enabled };
  }
  return { enabled: enabled ? true : config.enabled, plugins };
}

/**
 * Upsert a plugin entry's config/fixture/envMapping, preserving any field not
 * provided in `patch` and the existing `enabled`. New entries default to
 * `enabled: false`: `set` only configures; activation is always explicit.
 */
export function withPluginConfig(
  config: EmulationTemplateConfig,
  pluginId: string,
  patch: {
    config?: Record<string, unknown>;
    fixture?: unknown;
    envMapping?: Record<string, string | string[]>;
  }
): EmulationTemplateConfig {
  const plugins = [...(config.plugins ?? [])];
  const idx = plugins.findIndex((plugin) => plugin.pluginId === pluginId);
  const existing = idx === -1 ? undefined : plugins[idx];

  const merged: EmulationPluginEntry = {
    pluginId,
    enabled: existing?.enabled ?? false,
    config: patch.config !== undefined ? patch.config : (existing?.config ?? {}),
  };
  const fixture = patch.fixture !== undefined ? patch.fixture : existing?.fixture;
  if (fixture !== undefined) merged.fixture = fixture;
  const envMapping = patch.envMapping !== undefined ? patch.envMapping : existing?.envMapping;
  if (envMapping !== undefined) merged.envMapping = envMapping;

  if (idx === -1) plugins.push(merged);
  else plugins[idx] = merged;
  return { enabled: config.enabled, plugins };
}

/** Drop a plugin entry (callers validate existence first). */
export function withoutPlugin(
  config: EmulationTemplateConfig,
  pluginId: string
): EmulationTemplateConfig {
  return {
    enabled: config.enabled,
    plugins: (config.plugins ?? []).filter((plugin) => plugin.pluginId !== pluginId),
  };
}

async function handleEmulationGet(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const positional = argv.filter((arg) => !arg.startsWith('-'));
  const templateId = positional[0];
  const json = hasFlag(argv, '--json');

  if (!templateId) {
    console.error('Error: template ID required. Usage: canary sandbox emulation get <template-id>');
    process.exit(1);
  }

  const { config, catalog } = await fetchEmulation(apiUrl, token, templateId);
  const available = getAllPlugins().map((plugin) => ({ id: plugin.id, name: plugin.name }));

  if (json) {
    console.log(JSON.stringify({ config, catalog, availablePlugins: available }, null, 2));
    return;
  }

  console.log(`Emulation: ${config.enabled ? 'ENABLED' : 'disabled'} (master switch)`);
  const configured = config.plugins ?? [];
  if (configured.length === 0) {
    console.log('  No plugins configured.');
  } else {
    console.log(`  Configured plugins (${configured.length}):`);
    for (const plugin of configured) {
      const status = plugin.enabled ? '' : ' [disabled]';
      console.log(`    ${pad(plugin.pluginId, 16)}${status}`);
      const catalogEntry = catalog.find((entry) => entry.pluginId === plugin.pluginId);
      if (catalogEntry && catalogEntry.envContract.length > 0) {
        console.log(
          `      env contract: ${catalogEntry.envContract.map((entry) => entry.logicalKey).join(', ')}`
        );
      }
      if (catalogEntry && catalogEntry.actionInterfaces.length > 0) {
        const implemented = catalogEntry.actionInterfaces.filter((action) => action.implemented).length;
        console.log(
          `      action interfaces: ${implemented}/${catalogEntry.actionInterfaces.length} implemented`
        );
      }
      const keys = Object.keys(plugin.config ?? {});
      if (keys.length > 0) console.log(`      config keys: ${keys.join(', ')}`);
      const extras = [
        plugin.fixture !== undefined ? 'fixture' : null,
        plugin.envMapping ? 'envMapping' : null,
      ].filter(Boolean);
      if (extras.length > 0) console.log(`      set: ${extras.join(', ')}`);
    }
  }
  console.log(`  Available plugins: ${available.map((plugin) => plugin.id).join(', ')}`);
}

/** Shared enable/disable for the master switch or a single plugin entry. */
async function setEmulationEnabled(
  argv: string[],
  apiUrl: string,
  token: string,
  enable: boolean
): Promise<void> {
  const positional = argv.filter((arg) => !arg.startsWith('-'));
  const templateId = positional[0];
  const pluginId = positional[1];
  const verb = enable ? 'enable' : 'disable';

  if (!templateId) {
    console.error(
      `Error: template ID required. Usage: canary sandbox emulation ${verb} <template-id> [pluginId]`
    );
    process.exit(1);
  }

  const { config } = await fetchEmulation(apiUrl, token, templateId);
  let next: EmulationTemplateConfig;

  if (!pluginId) {
    next = withEmulationMaster(config, enable);
  } else {
    assertKnownPlugin(pluginId);
    const exists = (config.plugins ?? []).some((plugin) => plugin.pluginId === pluginId);
    if (!enable && !exists) {
      console.error(`Error: plugin '${pluginId}' is not configured on this template.`);
      process.exit(1);
    }
    next = withPluginEnabled(config, pluginId, enable);
  }

  await patchEmulation(apiUrl, token, templateId, next);
  console.log(pluginId ? `Plugin ${pluginId} ${verb}d` : `Emulation ${verb}d`);
}

async function handleEmulationSet(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const positional = argv.filter((arg) => !arg.startsWith('-'));
  const templateId = positional[0];
  const pluginId = positional[1];

  if (!templateId || !pluginId) {
    console.error('Error: template ID and pluginId required.');
    console.error(
      'Usage: canary sandbox emulation set <template-id> <pluginId> [--config <file>] [--fixture <file>] [--env-mapping <file>]'
    );
    process.exit(1);
  }
  assertKnownPlugin(pluginId);

  const configPath = getArgValue(argv, '--config');
  const fixturePath = getArgValue(argv, '--fixture');
  const envMappingPath = getArgValue(argv, '--env-mapping');

  if (!configPath && !fixturePath && !envMappingPath) {
    console.error('Error: provide at least one of --config, --fixture, --env-mapping');
    process.exit(1);
  }

  const pluginConfig = configPath ? readJsonObjectOrExit(configPath, '--config') : undefined;
  const fixture = fixturePath ? readJsonFileOrExit(fixturePath, '--fixture') : undefined;
  const envMapping = envMappingPath
    ? (readJsonObjectOrExit(envMappingPath, '--env-mapping') as Record<
        string,
        string | string[]
      >)
    : undefined;

  const { config } = await fetchEmulation(apiUrl, token, templateId);
  const next = withPluginConfig(config, pluginId, {
    ...(pluginConfig !== undefined ? { config: pluginConfig } : {}),
    ...(fixture !== undefined ? { fixture } : {}),
    ...(envMapping !== undefined ? { envMapping } : {}),
  });

  await patchEmulation(apiUrl, token, templateId, next);
  // A plugin only runs when BOTH the master switch and its own entry are on.
  // `set` never flips either, so point the user at `enable` unless it is already active.
  const entry = next.plugins.find((plugin) => plugin.pluginId === pluginId);
  const active = next.enabled && entry?.enabled;
  const hint = active ? '' : ` (inactive — run "emulation enable ${templateId} ${pluginId}" to activate)`;
  console.log(`Emulation plugin set: ${pluginId}${hint}`);
}

async function handleEmulationRemove(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const positional = argv.filter((arg) => !arg.startsWith('-'));
  const templateId = positional[0];
  const pluginId = positional[1];

  if (!templateId || !pluginId) {
    console.error(
      'Error: template ID and pluginId required. Usage: canary sandbox emulation remove <template-id> <pluginId>'
    );
    process.exit(1);
  }

  const { config } = await fetchEmulation(apiUrl, token, templateId);
  if (!(config.plugins ?? []).some((plugin) => plugin.pluginId === pluginId)) {
    console.error(`Error: plugin '${pluginId}' is not configured on this template.`);
    process.exit(1);
  }

  const next = withoutPlugin(config, pluginId);

  await patchEmulation(apiUrl, token, templateId, next);
  console.log(`Emulation plugin removed: ${pluginId}`);
}

export async function handleEmulation(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const [action, ...rest] = argv;
  switch (action) {
    case 'get':
      await handleEmulationGet(rest, apiUrl, token);
      break;
    case 'enable':
      await setEmulationEnabled(rest, apiUrl, token, true);
      break;
    case 'disable':
      await setEmulationEnabled(rest, apiUrl, token, false);
      break;
    case 'set':
      await handleEmulationSet(rest, apiUrl, token);
      break;
    case 'remove':
    case 'rm':
      await handleEmulationRemove(rest, apiUrl, token);
      break;
    default:
      console.error(
        `Unknown emulation action: ${action ?? '(none)'}. Use get/enable/disable/set/remove.`
      );
      process.exit(1);
  }
}
